import { useCallback, useEffect, useState } from "react";
import AvatarEditor from "react-avatar-editor";
import axios from "axios";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";

// My Files
import { encodeImage } from "../../utility";

const DEFAULT_POSITION = { x: 0.5, y: 0.5 };

const sliderStyles = {
    color: "primary.main",
    height: 5,
    py: 1,
    "& .MuiSlider-thumb": {
        width: 18,
        height: 18,
    },
    "& .MuiSlider-rail": {
        opacity: 0.24,
    },
};

export default function AvatarUploadButton(props) {
    const editorSize = props.compact ? 240 : 300;
    const editorBorder = props.compact ? 25 : 35;
    const avatarSize = props.compact ? 150 : 200;

    const [avatar, setAvatar] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState(DEFAULT_POSITION);

    useEffect(() => {
        if (!props.recipient) {
            return undefined;
        }

        if (props.recipient.avatar) {
            setAvatar(props.recipient.avatar);
            return undefined;
        }

        if (
            !props.recipient.imageURL ||
            props.recipient.imageURL === "none"
        ) {
            return undefined;
        }

        let isActive = true;

        axios
            .get(props.recipient.imageURL, {
                responseType: "arraybuffer",
            })
            .then((response) => {
                if (!isActive) {
                    return;
                }

                const buffer = Buffer.from(response.data, "base64");
                const photo = "data:image/png;base64," + encodeImage(buffer);

                setAvatar(photo);
                props.recipient.avatar = photo;
            })
            .catch((error) => {
                if (isActive) {
                    console.error(error);
                }
            });

        return () => {
            isActive = false;
        };
    }, [props.recipient]);

    // Keep the existing upload contract: parent screens read this editor and call
    // getImageScaledToCanvas() before sending the resulting blob to Firebase.
    const setEditorRef = useCallback(
        (editor) => {
            if (props.recipient) {
                props.recipient.avatarEditor = editor || undefined;
            }
        },
        [props.recipient],
    );

    const handleChoosePhotoClick = (event) => {
        const [file] = event.target.files || [];

        if (!file || typeof FileReader === "undefined") {
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setAvatar(reader.result);
            setScale(1);
            setPosition(DEFAULT_POSITION);
            setIsEditing(true);

            if (props.didChangeAvatar) {
                props.didChangeAvatar();
            }
        };
        reader.readAsDataURL(file);

        // Allow the same file to be selected again later.
        event.target.value = "";
    };

    const handleScaleChange = (event, newValue) => {
        setScale(Array.isArray(newValue) ? newValue[0] : newValue);
    };

    const handlePositionChange = (nextPosition) => {
        setPosition(nextPosition);
    };

    const fileInput = (
        <input
            accept="image/*"
            aria-label="Choose avatar image"
            hidden
            onChange={handleChoosePhotoClick}
            type="file"
        />
    );

    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: props.centered ? "center" : "flex-start",
            }}
        >
            {isEditing ? (
                <Stack
                    spacing={2}
                    alignItems="center"
                    sx={{ width: "100%", maxWidth: editorSize + editorBorder * 2 }}
                >
                    <Button
                        color="primary"
                        component="label"
                        startIcon={<PhotoCameraOutlinedIcon />}
                        variant="outlined"
                    >
                        Choose a different photo
                        {fileInput}
                    </Button>

                    <Box
                        sx={{
                            maxWidth: "100%",
                            overflow: "hidden",
                            lineHeight: 0,
                            borderRadius: 1,
                            "& canvas": {
                                display: "block",
                                maxWidth: "100%",
                                height: "auto !important",
                                cursor: "grab",
                                touchAction: "none",
                            },
                            "& canvas:active": {
                                cursor: "grabbing",
                            },
                        }}
                    >
                        <AvatarEditor
                            ref={setEditorRef}
                            width={editorSize}
                            height={editorSize}
                            image={avatar}
                            scale={scale}
                            position={position}
                            onPositionChange={handlePositionChange}
                            border={editorBorder}
                            borderRadius={editorSize / 2}
                            color={[20, 35, 25, 0.58]}
                        />
                    </Box>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.4, textAlign: "center" }}
                    >
                        Drag the photo to position it.
                    </Typography>

                    <Box sx={{ width: "100%" }}>
                        <Typography variant="body2" id="avatar-zoom-slider">
                            Zoom
                        </Typography>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <ZoomOutIcon color="action" fontSize="small" />
                            <Slider
                                value={scale}
                                onChange={handleScaleChange}
                                aria-labelledby="avatar-zoom-slider"
                                min={1}
                                max={3}
                                step={0.01}
                                sx={sliderStyles}
                            />
                            <ZoomInIcon color="action" fontSize="small" />
                        </Stack>
                    </Box>

                </Stack>
            ) : (
                <Button
                    component="label"
                    aria-label="Choose a profile picture"
                    sx={{
                        minWidth: 0,
                        p: 0,
                        borderRadius: "50%",
                        textTransform: "none",
                    }}
                >
                    <IconButton component="span" size="large" tabIndex={-1}>
                        <Avatar
                            alt="Profile Picture"
                            sx={{
                                height: avatarSize,
                                width: avatarSize,
                            }}
                            src={avatar}
                        >
                            {props.path === "users" ? (
                                <PersonIcon
                                    sx={{
                                        fontSize: props.compact ? 105 : 150,
                                    }}
                                />
                            ) : (
                                <GroupIcon
                                    sx={{
                                        fontSize: props.compact ? 105 : 150,
                                    }}
                                />
                            )}
                        </Avatar>
                    </IconButton>
                    {fileInput}
                </Button>
            )}
        </Box>
    );
}
